import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { Query, useMutation } from 'react-apollo';
import Spin from 'components/common/Spin';
import Select from 'components/form/Select';
import Button from 'components/common/Button';
import { EVENT_TRIGGERS } from 'constant/notifications';
import { USER_TYPES } from 'constant/userTypes';
import {
  GET_TEMPLATES,
  UPSERT_TEMPLATE,
  COPY_TEMPLATES,
  DELETE_TEMPLATE,
} from 'graphql/notification-templates';
import useSnack from 'hooks/useSnack';
import AppNotification from './components/AppNotification';
import SMSNotification from './components/SMSNotification';
import EmailNotification from './components/EmailNotification';
import ChatNotification from './components/ChatNotification';
import Variables from './components/Variables';
import Task from './components/Task';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
}));

const ROLES = Object.keys(USER_TYPES).map((key) => ({
  label: USER_TYPES[key],
  value: key,
}));

const NotificationTemplates = ({}) => {
  const classes = useStyles();
  const [trigger, setTrigger] = useState();
  const [role, setRole] = useState();
  const [copyTo, setCopyTo] = useState();
  const [upsertTemplate] = useMutation(UPSERT_TEMPLATE);
  const [copyMutation] = useMutation(COPY_TEMPLATES);
  const [deleteMutation] = useMutation(DELETE_TEMPLATE);
  const snack = useSnack();

  return (
    <Box>
      <Helmet title="Notification templates" />
      <Card className={classes.card}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={4}>
              <Select
                options={EVENT_TRIGGERS}
                label="Select a trigger"
                isMulti={false}
                onChange={setTrigger}
                value={trigger}
                transformValue
              />
            </Grid>
            <Grid item md={4}>
              <Select
                options={ROLES}
                label="Select recipient role"
                isMulti={false}
                onChange={setRole}
                value={role}
                transformValue
              />
            </Grid>
          </Grid>
          <Variables />
        </CardContent>
      </Card>
      {trigger && role ? (
        <Query
          query={GET_TEMPLATES}
          variables={{ trigger, role }}
          fetchPolicy={'cache-and-network'}
        >
          {({ data, loading, refetch }) => {
            if (loading) return <Spin spinning />;
            const { rows } = data;

            const onSave = (payload) => {
              return upsertTemplate({
                variables: {
                  data: payload,
                },
              }).then(
                () => {
                  if (!payload.id) {
                    refetch();
                  }
                  snack.success('Template saved');
                },
                (e) => {
                  snack.error(e.message);
                },
              );
            };

            const onDelete = (id) => {
              return deleteMutation({
                variables: { id }
              }).then(
                () => {
                  refetch();
                  snack.success('Template deleted');
                },
                (e) => {
                  snack.error(e.message);
                }
              )
            }

            const onCopy = () => {
              const payload = [];
              copyTo.forEach(role => {
                rows.forEach(i => {
                  payload.push({
                    body: i.body,
                    subject: i.subject,
                    recipient_role: role.value,
                    delivery_type: i.delivery_type,
                    event_trigger: i.event_trigger,
                    product_type: i.product_type,
                  })
                })
              });
              return copyMutation({
                variables: {
                  data: payload,
                },
              }).then(
                () => {
                  snack.success('Templates copied to target role.');
                  setCopyTo('');
                },
                (e) => {
                  snack.error(e.message);
                },
              );
            };

            return (
              <Box>
                <SMSNotification
                  trigger={trigger}
                  role={role}
                  onSave={onSave}
                  onDelete={onDelete}
                  data={rows.filter((i) => i.delivery_type === 'sms')}
                />
                <ChatNotification
                  trigger={trigger}
                  role={role}
                  onSave={onSave}
                  onDelete={onDelete}
                  data={rows.filter((i) => i.delivery_type === 'chat')}
                />
                <AppNotification
                    trigger={trigger}
                    role={role}
                    onSave={onSave}
                    onDelete={onDelete}
                    data={rows.filter((i) => i.delivery_type === 'app')}
                />
                <EmailNotification
                  trigger={trigger}
                  role={role}
                  onSave={onSave}
                  onDelete={onDelete}
                  data={rows.filter((i) => i.delivery_type === 'email')}
                />
                <Task
                  trigger={trigger}
                  role={role}
                  onSave={onSave}
                  onDelete={onDelete}
                  data={rows.find((i) => i.delivery_type === 'task')}
                />
                {rows.length ? (
                  <Box>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1">
                          Copy message templates to another role.
                        </Typography>
                        <Typography variant="body2">
                          Note: This will overwrite existing templates for the
                          selected trigger for the target role.
                        </Typography>
                         <br/>
                        <Select
                          options={ROLES.filter((i) => i.value !== role)}
                          label="Select target roles:"
                          onChange={setCopyTo}
                          value={copyTo}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onCopy}
                          disabled={!copyTo || !copyTo.length}
                        >
                          Copy
                        </Button>
                      </CardContent>
                    </Card>
                  </Box>
                ) : null}
              </Box>
            );
          }}
        </Query>
      ) : (
        <Typography>Please make selections</Typography>
      )}
    </Box>
  );
};

export default NotificationTemplates;
